import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart, Check } from "lucide-react";
import { toast } from "sonner";

const STYLES = {
    hero: "mt-3 ml-2 inline-flex items-center gap-2 text-sm font-bold pl-3 pr-4 py-2 rounded-full shadow-lg hover:-translate-y-0.5 transition-all ring-1 ring-white/40",
    card: "inline-flex items-center justify-center gap-1.5 text-sm font-bold px-4 h-9 rounded-full border transition-colors",
};

// Follows the pin's owner — their new products, gigs and posts land in your feed.
export const FollowPinButton = ({ listing, variant = "hero" }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [following, setFollowing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [followers, setFollowers] = useState(0);
    const ownerId = listing?.owner_id;
    const hidden = !ownerId || listing.claim_status === "unclaimed" || user?.user_id === ownerId;

    useEffect(() => {
        if (hidden || !user) return;
        api.get(`/follow/status/${ownerId}`).then(({ data }) => { setFollowing(!!data.following); setFollowers(data.followers || 0); }).catch(() => {});
    }, [ownerId, hidden, user]);

    if (hidden) return null;

    const toggle = async (e) => {
        e.stopPropagation();
        if (!user) { navigate("/auth"); return; }
        setBusy(true);
        try {
            if (following) { await api.delete(`/follow/${ownerId}`); setFollowing(false); setFollowers((n) => Math.max(0, n - 1)); toast.success(`Unfollowed ${listing.name}`); }
            else { await api.post(`/follow/${ownerId}`); setFollowing(true); setFollowers((n) => n + 1); toast.success(`Following ${listing.name} — their news will show in your feed`); }
        } catch (err) { toast.error(apiError(err)); }
        finally { setBusy(false); }
    };

    const look = variant === "hero"
        ? (following ? "bg-white text-primary" : "bg-primary text-white")
        : (following ? "border-primary text-primary bg-primary/5" : "border-primary bg-primary/10 text-primary hover:bg-primary/15");

    return (
        <button type="button" onClick={toggle} disabled={busy} data-testid={`follow-pin-btn-${variant}`}
            aria-pressed={following} className={`${STYLES[variant]} ${look}`}>
            {following ? <Check size={14} /> : <Heart size={14} />} {following ? "Following" : "Follow"}
            {followers > 0 && <span data-testid="follow-pin-count" className="opacity-75 font-semibold">· {followers}</span>}
        </button>
    );
};
